import React, { FC, useContext } from 'react';
import EmptyState from '@atlaskit/empty-state';
import {
  SearchResultDTOTypeEnum,
  SearchResultDTO,
  PageSearchResultDTO,
} from '@atlassiansox/phobos-client-ts';
import { ExperienceSuccess as UFOExperienceSuccess } from '@atlassian/ufo';

import { searchViewExperience } from '@/common/ufo/experiences';
import {
  positionRelative,
  spaceTopComfortable,
  spaceTopCozy,
  spaceTopMinimum,
  tableHeadStyles,
  tableRowsStyles,
} from '@/styles';
import CustomIcon from '@/components/primitive/icon';
import { SearchConfigContext, SearchViewType } from '@/controllers/search-config-controller';

import * as stylesSearch from '../search-layouts/styles';
import { testIds } from '../constants';

import * as styles from './styles';
import SearchPageSkeleton from './search-page-skeleton';
import SearchListItem from './search-list-item';

type SearchResultsProps = {
  isLoading: boolean;
  data: PageSearchResultDTO;
  isSearchEnabled: boolean;
};

const getSearchResultHead = (isComfortView: boolean) => (
  <div
    css={[
      tableHeadStyles,
      stylesSearch.searchResultRowStyles,
      isComfortView ? stylesSearch.searchResultRowComfortStyles : '',
    ]}
  >
    <div>Type</div>
    <div>Title</div>
    <div>Status</div>
    {!isComfortView && <div>Info</div>}
  </div>
);

const SearchResults: FC<SearchResultsProps> = ({ data, isLoading, isSearchEnabled }) => {
  const { searchViewType } = useContext(SearchConfigContext);
  const isComfortView = searchViewType === SearchViewType.ComfortView;

  if (isLoading) {
    return (
      <div css={stylesSearch.searchResultStyles}>
        {getSearchResultHead(isComfortView)}
        <div css={spaceTopCozy}>
          <SearchPageSkeleton />
        </div>
      </div>
    );
  }

  if (!isSearchEnabled) {
    return (
      <div css={stylesSearch.searchResultStyles} data-testid={testIds.searchNotApplied}>
        <div css={[spaceTopComfortable, positionRelative]}>
          <EmptyState
            header="Start searching on Tome..."
            description="Please select filter type criteria or enter a search term"
            imageUrl="/static/logo-raw.svg"
          />
          <span css={styles.starSpinnerStyles}>
            <CustomIcon className="star-spinner" src="/static/logo-star.svg" />
          </span>
        </div>
        {/* We no longer load any content when no filters are applied to the search,
          so we initiate the load time measurement immediately. */}
        <UFOExperienceSuccess experience={searchViewExperience} />
      </div>
    );
  }

  return (
    <div css={stylesSearch.searchResultStyles} data-testid={testIds.searchResults}>
      {getSearchResultHead(isComfortView)}
      {data?.content?.length === 0 && (
        <EmptyState
          header="We couldn't find anything matching your search"
          description="Try again with a different term, or change filter criteria"
          imageUrl="/static/not-found.svg"
        />
      )}
      <ul css={[spaceTopMinimum, tableRowsStyles, styles.searchResultRowsStyles]}>
        {data?.content?.map((datum: SearchResultDTO) => {
          const searchResultType: SearchResultDTOTypeEnum | undefined = datum.type;
          if (!searchResultType) return <></>;

          let item;
          switch (searchResultType) {
            case SearchResultDTOTypeEnum.Slo:
              item = datum.slo;
              break;
            case SearchResultDTOTypeEnum.SloGroup:
              // The key for SLO Group search type is still slo, not sloGroup
              item = datum.slo;
              break;
            case SearchResultDTOTypeEnum.Capability:
              item = datum.capability;
              break;
            case SearchResultDTOTypeEnum.Team:
              item = datum.team;
              break;
            case SearchResultDTOTypeEnum.Product:
              item = datum.product;
              break;
          }
          if (!item) return <></>;

          return <SearchListItem type={searchResultType} key={item.publicId} item={item} />;
        })}
      </ul>
      {/* This is still needed to measure the loading time for direct visits with params in the URL */}
      {/* e.g: /?byTeam=true */}
      {/* isLoading can be false when react-query fetchStatus is 'idle' on transition */}
      {data && <UFOExperienceSuccess experience={searchViewExperience} />}
    </div>
  );
};

export default SearchResults;
